// TTS Audio Service for PWA
export class TTSAudioService {
  constructor() {
    this.audioBuffers = new Map();
    this.pendingAudioChunks = new Map();
    this.pendingWavChunks = new Map();  // WAV chunks as fallback
    this.autoPlayEnabled = true;
    this.currentAudio = null;
    this.audioQueue = new Map();
    this.playbackStates = new Map();
    this.playbackQueue = [];
    this.isProcessingQueue = false;
    this.playbackPaused = false;
    this._speechStartTime = null;
    this._pauseDebounceTimer = null;
    this.debugEnabled = localStorage.getItem('polyglot-debug-tts') === 'true' ||
      localStorage.getItem('polyglot-debug') === 'true';
  }

  debugLog(...args) {
    if (this.debugEnabled) {
      this.debugLog(...args);
    }
  }

  setAutoPlay(enabled) {
    this.autoPlayEnabled = enabled;
    this.debugLog(`TTS: Auto-play ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Called when user starts speaking — debounced 200ms to filter VAD false starts.
  // If speech is sustained, pauses any playing audio and blocks queue processing.
  pausePlayback() {
    this._speechStartTime = Date.now();
    clearTimeout(this._pauseDebounceTimer);
    this._pauseDebounceTimer = setTimeout(() => {
      if (!this.playbackPaused) {
        this.playbackPaused = true;
        if (this.currentAudio && !this.currentAudio.paused) {
          this.currentAudio.pause();
          this.debugLog('TTS: Playback paused (user speaking)');
        }
      }
    }, 200);
  }

  // Called when user stops speaking — resumes audio and unblocks the queue.
  resumePlayback() {
    clearTimeout(this._pauseDebounceTimer);
    this._speechStartTime = null;
    if (!this.playbackPaused) return;
    this.playbackPaused = false;
    if (this.currentAudio && this.currentAudio.paused && !this.currentAudio.ended) {
      this.currentAudio.play().catch(e => console.warn('TTS: Resume play failed:', e));
      this.debugLog('TTS: Playback resumed (user stopped speaking)');
    }
  }

  isAutoPlayEnabled() {
    return this.autoPlayEnabled;
  }

  async initialize() {
    this.debugLog('TTS: Service initialized');
  }

  getPlaybackState(messageId) {
    const state = this.playbackStates.get(messageId);
    if (!state) {
      return { isPlaying: false, isPaused: false };
    }
    
    if (!state.audio) {
      const isBrowserTTSSpeaking = 'speechSynthesis' in window && speechSynthesis.speaking;
      const isBrowserTTSPaused = 'speechSynthesis' in window && speechSynthesis.paused;
      
      return {
        isPlaying: isBrowserTTSSpeaking,
        isPaused: isBrowserTTSPaused
      };
    }
    
    const audio = state.audio;
    const isActuallyPlaying = !audio.paused && !audio.ended && audio.readyState >= 2;
    const isActuallyPaused = audio.paused && !audio.ended;
    
    return {
      isPlaying: isActuallyPlaying,
      isPaused: isActuallyPaused
    };
  }

  stopMessage(messageId) {
    const state = this.playbackStates.get(messageId);
    if (!state) return;
    
    if (state.audio) {
      state.audio.pause();
      state.audio.currentTime = 0;
      if (this.currentAudio === state.audio) this.currentAudio = null;
      this.debugLog(`TTS: Stopped HTML audio for message ${messageId}`);
    } else {
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
        this.debugLog(`TTS: Stopped browser TTS for message ${messageId}`);
      }
    }

    if (state.resolve) state.resolve();
    this.playbackStates.delete(messageId);
  }

  async handleTTSAudioChunk(data) {
    try {
      const { messageId, chunk, wavChunk, isLast, languageCode, text, isPauseBoundary, isOriginal, isPartialAudio } = data;
      const audioPartIndex = Number.isInteger(data.audioPartIndex) ? data.audioPartIndex : 0;

      this.debugLog(`TTS: Received audio chunk for message ${messageId}`);
      this.debugLog(`TTS: Language: ${languageCode}, Original: ${isOriginal}, isLast: ${isLast}, isPartialAudio: ${isPartialAudio}`);

      // Use different keys for partial/final + part index so multi-part streaming
      // chunks are assembled and queued independently.
      // Example: messageId_languageCode_partial_p0, ..._partial_p1, ..._final_p9
      const chunkType = isPartialAudio ? 'partial' : 'final';
      const audioKey = `${messageId}_${languageCode}_${chunkType}_p${audioPartIndex}`;

      // Guard against duplicate processing from multiple active listeners or socket
      // reconnect replay: if audio for this key is already fully assembled and stored,
      // and there is no in-progress pending stream, ignore the incoming chunk.
      if (this.audioQueue.has(audioKey) && !this.pendingAudioChunks.has(audioKey)) {
        this.debugLog(`TTS: Ignoring duplicate chunk for already-assembled key ${audioKey}`);
        return;
      }

      if (!this.pendingAudioChunks.has(audioKey)) {
        this.pendingAudioChunks.set(audioKey, []);
      }
      if (!this.pendingWavChunks.has(audioKey)) {
        this.pendingWavChunks.set(audioKey, []);
      }
      
      this.pendingAudioChunks.get(audioKey).push(chunk);
      if (wavChunk) {
        this.pendingWavChunks.get(audioKey).push(wavChunk);
      }
      
      if (isLast) {
        const allChunks = this.pendingAudioChunks.get(audioKey);
        const totalLength = allChunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const combinedAudio = new Uint8Array(totalLength);
        
        let offset = 0;
        for (const chunk of allChunks) {
          combinedAudio.set(chunk, offset);
          offset += chunk.length;
        }
        
        this.debugLog(`TTS: Combined audio for ${audioKey}: ${combinedAudio.length} bytes`);
        
        // Also combine WAV chunks as fallback
        let combinedWav = null;
        const allWavChunks = this.pendingWavChunks.get(audioKey);
        if (allWavChunks && allWavChunks.length > 0) {
          const wavTotalLength = allWavChunks.reduce((sum, c) => sum + c.length, 0);
          combinedWav = new Uint8Array(wavTotalLength);
          let wavOffset = 0;
          for (const c of allWavChunks) {
            combinedWav.set(c, wavOffset);
            wavOffset += c.length;
          }
          this.debugLog(`TTS: Combined WAV fallback for ${audioKey}: ${combinedWav.length} bytes`);
        }
        
        // Store the audio with the exact key format used for playback
        // Include the chunk type (partial/final) so both can coexist
        this.storeAudioForMessage(audioKey, combinedAudio, languageCode, text, combinedWav);
        
        // Also store with just the messageId as a fallback (for backward compatibility)
        // Only for final chunks to avoid playback confusion
        if (!isPartialAudio) {
          this.storeAudioForMessage(messageId, combinedAudio, languageCode, text, combinedWav);
        }
        
        // Clean up pending chunks
        this.pendingAudioChunks.delete(audioKey);
        this.pendingWavChunks.delete(audioKey);
        
        // Note: Auto-play is now handled in the group session logic 
        // to ensure it plays in the listener's preferred language
      }
    } catch (error) {
      console.error('TTS: Error handling audio chunk:', error);
    }
  }

  async playAudioBytes(audioBytes, messageId, languageCode, wavFallbackBytes = null) {
    try {
      const langInfo = languageCode ? ` (${languageCode})` : '';
      this.debugLog(`TTS: Playing server-generated audio for message ${messageId}${langInfo}, ${audioBytes.length} bytes`);

      const audioBlob = this.createAudioBlob(audioBytes);
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve, reject) => {
        this.playbackStates.set(messageId, { isPlaying: false, audio, resolve });

        audio.onended = () => {
          this.debugLog(`TTS: Audio playback finished for message ${messageId}${langInfo}`);
          URL.revokeObjectURL(audioUrl);
          this.playbackStates.delete(messageId);
          if (this.currentAudio === audio) {
            this.currentAudio = null;
          }
          resolve();
        };
        
        audio.onerror = async (error) => {
          console.warn(`TTS: Primary audio format failed for ${messageId}${langInfo}, trying WAV fallback...`);
          URL.revokeObjectURL(audioUrl);
          
          // Try WAV fallback if available
          if (wavFallbackBytes && wavFallbackBytes.length > 0) {
            try {
              const wavBlob = new Blob([wavFallbackBytes], { type: 'audio/wav' });
              const wavUrl = URL.createObjectURL(wavBlob);
              const wavAudio = new Audio(wavUrl);
              
              this.playbackStates.set(messageId, { isPlaying: false, audio: wavAudio, resolve });
              this.currentAudio = wavAudio;

              wavAudio.onended = () => {
                this.debugLog(`TTS: WAV fallback playback finished for ${messageId}${langInfo}`);
                URL.revokeObjectURL(wavUrl);
                this.playbackStates.delete(messageId);
                if (this.currentAudio === wavAudio) this.currentAudio = null;
                resolve();
              };
              wavAudio.onerror = (wavError) => {
                console.error(`TTS: WAV fallback also failed for ${messageId}${langInfo}:`, wavError);
                URL.revokeObjectURL(wavUrl);
                this.playbackStates.delete(messageId);
                if (this.currentAudio === wavAudio) this.currentAudio = null;
                reject(wavError);
              };
              
              await wavAudio.play();
              this.debugLog(`TTS: WAV fallback playing for ${messageId}${langInfo}`);
              return;
            } catch (wavErr) {
              console.error(`TTS: WAV fallback play() failed for ${messageId}${langInfo}:`, wavErr);
            }
          }
          
          this.playbackStates.delete(messageId);
          if (this.currentAudio === audio) this.currentAudio = null;
          reject(error);
        };
        
        this.currentAudio = audio;
        
        audio.play().catch(async (error) => {
          console.warn(`TTS: Primary play() failed for ${messageId}${langInfo}: ${error.message}`);
          URL.revokeObjectURL(audioUrl);
          
          // Try WAV fallback
          if (wavFallbackBytes && wavFallbackBytes.length > 0) {
            try {
              this.debugLog(`TTS: Attempting WAV fallback for ${messageId}${langInfo}`);
              const wavBlob = new Blob([wavFallbackBytes], { type: 'audio/wav' });
              const wavUrl = URL.createObjectURL(wavBlob);
              const wavAudio = new Audio(wavUrl);
              
              this.playbackStates.set(messageId, { isPlaying: false, audio: wavAudio, resolve });
              this.currentAudio = wavAudio;

              await new Promise((res, rej) => {
                wavAudio.onended = () => {
                  URL.revokeObjectURL(wavUrl);
                  this.playbackStates.delete(messageId);
                  if (this.currentAudio === wavAudio) this.currentAudio = null;
                  res();
                };
                wavAudio.onerror = (e) => {
                  URL.revokeObjectURL(wavUrl);
                  this.playbackStates.delete(messageId);
                  if (this.currentAudio === wavAudio) this.currentAudio = null;
                  rej(e);
                };
                wavAudio.play().catch(rej);
              });
              this.debugLog(`TTS: WAV fallback finished for ${messageId}${langInfo}`);
              resolve();
              return;
            } catch (wavErr) {
              console.error(`TTS: WAV fallback also failed:`, wavErr);
            }
          }
          
          this.playbackStates.delete(messageId);
          this.currentAudio = null;
          reject(error);
        });
      });
    } catch (error) {
      console.error('TTS: Error playing audio bytes:', error);
      throw error;
    }
  }

  createAudioBlob(audioBytes) {
    const header = Array.from(audioBytes.slice(0, 12));
    
    if (header[0] === 0x1A && header[1] === 0x45 && header[2] === 0xDF && header[3] === 0xA3) {
      this.debugLog('TTS: Detected WebM format');
      return new Blob([audioBytes], { type: 'audio/webm' });
    }
    
    if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46 &&
        header[8] === 0x57 && header[9] === 0x41 && header[10] === 0x56 && header[11] === 0x45) {
      this.debugLog('TTS: Detected WAV format');
      return new Blob([audioBytes], { type: 'audio/wav' });
    }
    
    if ((header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33) ||
        (header[0] === 0xFF && (header[1] & 0xE0) === 0xE0)) {
      this.debugLog('TTS: Detected MP3 format');
      return new Blob([audioBytes], { type: 'audio/mpeg' });
    }
    
    if (header[0] === 0x4F && header[1] === 0x67 && header[2] === 0x67 && header[3] === 0x53) {
      this.debugLog('TTS: Detected OGG format');
      return new Blob([audioBytes], { type: 'audio/ogg' });
    }
    
    this.debugLog('TTS: Using default WebM format, header:', header);
    return new Blob([audioBytes], { type: 'audio/webm' });
  }

  storeAudioForMessage(audioKey, audioBytes, languageCode, text, wavFallbackBytes = null) {
    const audioBlob = this.createAudioBlob(audioBytes);
    const audioUrl = URL.createObjectURL(audioBlob);
    this.audioQueue.set(audioKey, { 
      blob: audioBlob, 
      url: audioUrl,
      languageCode,
      text,
      wavFallbackBytes
    });
    this.debugLog(`TTS: Stored audio for key ${audioKey}${languageCode ? ` (${languageCode})` : ''}`);
  }

  getLatestPartKey(prefix) {
    let latestKey = null;
    let latestIndex = -1;

    for (const key of this.audioQueue.keys()) {
      if (!key.startsWith(`${prefix}_p`)) continue;
      const match = key.match(/_p(\d+)$/);
      if (!match) continue;
      const idx = parseInt(match[1], 10);
      if (Number.isNaN(idx)) continue;
      if (idx > latestIndex) {
        latestIndex = idx;
        latestKey = key;
      }
    }

    return latestKey;
  }

  async playMessageAudio(messageId, text, languageCode) {
    try {
      this.debugLog(`TTS: Server audio playback requested for message ${messageId}: "${text}" in ${languageCode}`);
      
      // Try to play final chunk first, then fall back to partial or just messageId
      const latestFinalKey = this.getLatestPartKey(`${messageId}_${languageCode}_final`);
      const latestPartialKey = this.getLatestPartKey(`${messageId}_${languageCode}_partial`);

      let storedAudio = latestFinalKey ? this.audioQueue.get(latestFinalKey) : null;
      if (!storedAudio) {
        storedAudio = latestPartialKey ? this.audioQueue.get(latestPartialKey) : null;
      }
      if (!storedAudio) {
        storedAudio = this.audioQueue.get(`${messageId}_${languageCode}`);
      }
      if (!storedAudio) {
        storedAudio = this.audioQueue.get(messageId);
      }
      
      if (storedAudio) {
        const audioKey = `${messageId}_${languageCode}`;
        this.debugLog(`TTS: Playing stored server audio for ${audioKey}`);
        this.debugLog(`TTS: Stored audio language: ${storedAudio.languageCode}`);
        this.debugLog(`TTS: Stored audio text: "${storedAudio.text}"`);
        
        // Play the server audio regardless of language match, with WAV fallback
        const arrayBuffer = await storedAudio.blob.arrayBuffer();
        const audioBytes = new Uint8Array(arrayBuffer);
        await this.playAudioBytes(audioBytes, audioKey, storedAudio.languageCode, storedAudio.wavFallbackBytes);
        return;
      }
      
      // If no stored audio, check if it's currently being processed
      const hasPendingPart = Array.from(this.pendingAudioChunks.keys()).some(
        (k) => k.startsWith(`${messageId}_${languageCode}_partial_p`) || k.startsWith(`${messageId}_${languageCode}_final_p`)
      );
      if (hasPendingPart || this.pendingAudioChunks.has(`${messageId}_${languageCode}`) || this.pendingAudioChunks.has(messageId)) {
        this.debugLog(`TTS: Audio for message ${messageId} is still being processed, please wait`);
        return;
      }
      
      // NO BROWSER TTS FALLBACK - only server audio
      const audioKey = `${messageId}_${languageCode}`;
      this.debugLog(`TTS: No server audio available for ${audioKey} - audio playback not available`);
      throw new Error(`No server audio available for ${audioKey}`);
      
    } catch (error) {
      console.error('TTS: Server audio playback error:', error);
      throw error;
    }
  }

  hasStoredAudio(audioKey) {
    let hasAudio = this.audioQueue.has(audioKey);
    
    // Check for partial and final variants, including multi-part keys
    if (!hasAudio) {
      hasAudio = this.audioQueue.has(`${audioKey}_partial`) || this.audioQueue.has(`${audioKey}_final`);
    }
    if (!hasAudio) {
      hasAudio = !!this.getLatestPartKey(`${audioKey}_partial`) || !!this.getLatestPartKey(`${audioKey}_final`);
    }
    
    // If not found with the original key, try with just the messageId (fallback)
    if (!hasAudio && audioKey.includes('_')) {
      const messageId = audioKey.split('_')[0];
      hasAudio = this.audioQueue.has(messageId);
    }
    
    if (!hasAudio) {
      this.debugLog(`TTS: No stored audio found for key ${audioKey}`);
      this.debugLog(`TTS: Available audio keys:`, Array.from(this.audioQueue.keys()));
    }
    return hasAudio;
  }

  async playStoredAudio(audioKey) {
    try {
      // Look up audio, preferring final chunks over partial (streaming path uses suffixed keys)
      let storedAudio = this.audioQueue.get(audioKey);

      if (!storedAudio) {
        storedAudio = this.audioQueue.get(`${audioKey}_final`);
        if (storedAudio) this.debugLog(`TTS: Using _final variant for key ${audioKey}`);
      }
      if (!storedAudio) {
        const latestFinalKey = this.getLatestPartKey(`${audioKey}_final`);
        if (latestFinalKey) {
          storedAudio = this.audioQueue.get(latestFinalKey);
          this.debugLog(`TTS: Using latest _final part ${latestFinalKey} for key ${audioKey}`);
        }
      }
      if (!storedAudio) {
        storedAudio = this.audioQueue.get(`${audioKey}_partial`);
        if (storedAudio) this.debugLog(`TTS: Using _partial variant for key ${audioKey}`);
      }
      if (!storedAudio) {
        const latestPartialKey = this.getLatestPartKey(`${audioKey}_partial`);
        if (latestPartialKey) {
          storedAudio = this.audioQueue.get(latestPartialKey);
          this.debugLog(`TTS: Using latest _partial part ${latestPartialKey} for key ${audioKey}`);
        }
      }

      // If not found with the original key, try with just the messageId (fallback)
      if (!storedAudio && audioKey.includes('_')) {
        const messageId = audioKey.split('_')[0];
        storedAudio = this.audioQueue.get(messageId);
        if (storedAudio) {
          this.debugLog(`TTS: Using fallback audio key ${messageId} for ${audioKey}`);
        }
      }

      if (storedAudio) {
        this.debugLog(`TTS: Playing stored server audio for key ${audioKey}`);

        // Convert blob back to bytes and play, with WAV fallback
        const arrayBuffer = await storedAudio.blob.arrayBuffer();
        const audioBytes = new Uint8Array(arrayBuffer);
        await this.playAudioBytes(audioBytes, audioKey, storedAudio.languageCode, storedAudio.wavFallbackBytes);
      } else {
        this.debugLog(`TTS: No stored server audio found for key ${audioKey}`);
        throw new Error(`No server audio available for ${audioKey}`);
      }
    } catch (error) {
      console.error(`TTS: Error playing stored audio for key ${audioKey}:`, error);
      throw error;
    }
  }

  queueAudioForPlayback(audioKey, callbacks = null) {
    this.debugLog(`TTS: Queueing audio for playback: ${audioKey}`);
    this.playbackQueue.push({ audioKey, callbacks });
    this.debugLog(`TTS: Queue length: ${this.playbackQueue.length}`);

    // Start processing the queue if not already processing
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.isProcessingQueue) {
      this.debugLog('TTS: Queue already being processed');
      return;
    }

    if (this.playbackQueue.length === 0) {
      this.debugLog('TTS: Queue is empty');
      return;
    }

    this.isProcessingQueue = true;
    this.debugLog(`TTS: Starting queue processing, ${this.playbackQueue.length} items in queue`);

    while (this.playbackQueue.length > 0) {
      // Wait while user is speaking before pulling the next item
      while (this.playbackPaused) {
        await new Promise(r => setTimeout(r, 100));
      }

      const { audioKey, callbacks } = this.playbackQueue.shift();
      this.debugLog(`TTS: Processing queued audio: ${audioKey}, ${this.playbackQueue.length} remaining`);

      if (callbacks?.onStart) callbacks.onStart();
      try {
        await this.playStoredAudio(audioKey);
        this.debugLog(`TTS: Finished playing ${audioKey}`);
      } catch (error) {
        console.error(`TTS: Error playing queued audio ${audioKey}:`, error);
      } finally {
        if (callbacks?.onEnd) callbacks.onEnd();
      }
    }

    this.isProcessingQueue = false;
    this.debugLog('TTS: Queue processing completed');
  }

  clearQueue() {
    this.debugLog(`TTS: Clearing queue, ${this.playbackQueue.length} items removed`);
    this.playbackQueue = [];
  }
  cleanup() {
    this.playbackStates.forEach((state) => {
      if (state.audio) {
        state.audio.pause();
        state.audio.currentTime = 0;
      }
    });

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    this.playbackStates.clear();
    this.pendingAudioChunks.clear();
    this.pendingWavChunks.clear();

    this.audioQueue.forEach((stored) => {
      URL.revokeObjectURL(stored.url);
    });
    this.audioQueue.clear();

    // Clear the playback queue
    this.clearQueue();
    this.isProcessingQueue = false;
    this.playbackPaused = false;
    clearTimeout(this._pauseDebounceTimer);
    this._pauseDebounceTimer = null;
    this._speechStartTime = null;

    this.debugLog('TTS: Service cleaned up');
  }
}

export const ttsAudioService = new TTSAudioService();
